import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export const PRODUCT_LIST_PATH = '/development-order-overview';
const PRODUCT_CREATE_PATH = `${PRODUCT_LIST_PATH}/create`;
const PRODUCT_EDIT_PATH = `${PRODUCT_LIST_PATH}/edit`;

export interface ProductEditNavigationState {
  returnTo?: string;
}

export interface ProductEditNavigation {
  goToProductCreate: () => void;
  goToProductEdit: (devOrderId?: string | null) => void;
  goToProductDuplicate: (devOrderId?: string | null) => void;
}

/**
 * Navigation helpers for the full-page Create / Edit Product screen.
 *
 * The product add / edit / duplicate flows moved off the in-page `ProductModal` onto the
 * `/development-order-overview/create` and `/development-order-overview/edit/:devOrderId`
 * routes. Every caller passes its own location as `returnTo` router state so that Save and
 * Cancel on the product screen return the user to the page they left, rather than always
 * dropping them on the product list.
 * @returns {ProductEditNavigation} callbacks for the create, edit and duplicate routes
 */
export function useProductEditNavigation(): ProductEditNavigation {
  const history = useHistory();
  const { pathname, search } = useLocation();

  const pushWithReturn = useCallback(
    (pathTarget: string) => {
      history.push(pathTarget, { returnTo: `${pathname}${search}` });
    },
    [history, pathname, search],
  );

  const goToProductCreate = useCallback(() => {
    pushWithReturn(PRODUCT_CREATE_PATH);
  }, [pushWithReturn]);

  const goToProductEdit = useCallback(
    (devOrderId?: string | null) => {
      if (!devOrderId) return;
      pushWithReturn(`${PRODUCT_EDIT_PATH}/${devOrderId}`);
    },
    [pushWithReturn],
  );

  const goToProductDuplicate = useCallback(
    (devOrderId?: string | null) => {
      if (!devOrderId) return;
      history.push(`${PRODUCT_CREATE_PATH}?duplicateId=${devOrderId}`, { returnTo: `${pathname}${search}` });
    },
    [history, pathname, search],
  );

  return { goToProductCreate, goToProductEdit, goToProductDuplicate };
}

export default useProductEditNavigation;
